/**
 * SCRATCH — a true manual playhead.
 *
 * Not a rate-modulated AudioBufferSourceNode: Web Audio cannot play one
 * backwards, and a scratch that cannot reverse is not a scratch. The playhead
 * lives in public/worklets/scratch-processor.js (a real module file, never a
 * Blob URL) and interpolates samples itself, so the rate is signed, continuous
 * and audible from -8 to +8.
 *
 * The material is generated, not sampled: five stabs rendered into an
 * OfflineAudioContext at start. The classic battle-record vocal is present in
 * the manifest as a DISABLED entry and stays that way until the user supplies
 * their own licensed or self-recorded file plus provenance.
 *
 * Gesture law (src/mapping/angular.ts, pure and tested): angular velocity is
 * the signed rate, holding still approaches zero, reverse motion goes negative,
 * release eases to 1, and the cut gesture mutes rapidly without touching rate.
 */

import { createScratchEngine, type ScratchEngine } from '../audio/scratch/engine';
import { SCRATCH_MANIFEST, validateScratchManifest } from '../audio/scratch/manifest';
import { renderStab, STAB_IDS, STAB_LABELS, type StabId } from '../audio/scratch/stabs';
import { ScratchAdapter } from '../mapping/adapters';
import type { ControlState, FeatureFrame, ScratchIntent } from '../mapping/types';
import { chips, el, panel, slider, toggle } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createScratchScene(): Scene {
  let engine: ScratchEngine | null = null;
  let ctxRef: SceneContext | null = null;
  let current: StabId = 'ahh';
  let cut = false;
  let level = 0.85;
  let playhead = 0;
  const buffers = new Map<StabId, AudioBuffer>();
  const adapter = new ScratchAdapter();
  let last: ScratchIntent | null = null;
  let ready = false;

  async function select(id: StabId): Promise<void> {
    if (!ctxRef || !engine) return;
    current = id;
    let buf = buffers.get(id);
    if (!buf) {
      buf = await renderStab(ctxRef.bus.ctx.sampleRate, id);
      buffers.set(id, buf);
    }
    if (!engine) return; // unmounted during the offline render
    engine.load(buf);
    engine.start(0);
    engine.setLoop(true);
  }

  return {
    id: 'scratch',
    label: 'Scratch',
    blurb: 'A manual playhead on a worklet. Press and spin the platter — velocity is the rate, backwards is backwards, let go and it eases to 1x.',
    padMode: 'platter',

    async mount(host, ctx) {
      ctxRef = ctx;
      const issues = validateScratchManifest(SCRATCH_MANIFEST);
      const rateRead = el('b', { class: 'read wide' }, ['1.00x']);
      const phRead = el('div', { class: 'meter' }, [el('i', { class: 'meter-fill' })]);

      const body: HTMLElement[] = [
        chips<StabId>({
          label: 'Stab', value: current,
          options: STAB_IDS.map((id) => ({ id, label: STAB_LABELS[id] })),
          onChange: (id) => { void select(id); },
        }),
        el('div', { class: 'field' }, [
          el('span', { class: 'slabel' }, ['Rate']), rateRead,
        ]),
        el('div', { class: 'field' }, [
          el('span', { class: 'slabel' }, ['Playhead']), phRead,
        ]),
        slider({ label: 'Level', min: 0, max: 1, step: 0.01, value: level, format: (v) => v.toFixed(2), onInput: (v) => { level = v; } }),
        el('div', { class: 'row' }, [
          toggle('Cut', false, (on) => { cut = on; on ? engine?.cut() : engine?.uncut(); }, 'Rapid mute — the fader hand'),
          toggle('Loop', true, (on) => engine?.setLoop(on)),
        ]),
        el('div', { class: 'disabled-entry' }, [
          el('h3', {}, ['OH YEAH — disabled']),
          el('p', {}, [
            'The battle-record vocal is a manifest entry only. This repo ships no recording and asserts no licence for one. ',
            'Add a file you are entitled to use at ',
            el('code', {}, ['public/audio/scratch/oh-yeah.wav']),
            ', then call ',
            el('code', {}, ['enableRecorded()']),
            ' with its source and licence. Until both exist, the entry stays disabled and the scene runs on generated stabs.',
          ]),
        ]),
        issues.length
          ? el('p', { class: 'note warn' }, [`manifest: ${issues.map((i) => `${i.id} — ${i.message}`).join('; ')}`])
          : el('p', { class: 'note' }, ['manifest: valid — 5 generated sources, 1 disabled recorded entry']),
      ];

      host.append(panel('Scratch', 'The playhead is computed sample by sample with linear interpolation, so the rate can be negative.', body));

      try {
        const built = await createScratchEngine(ctx.bus.ctx, ctx.bus.input);
        // A fast scene switch unmounts while the worklet loads. The unmount ran
        // with engine === null, so nothing tore this down — dispose it here or
        // it loops a stab into the master bus forever, unreachable.
        if (ctxRef !== ctx) { built.stop(); built.dispose(); return; }
        engine = built;
        engine.onPlayhead((p) => {
          playhead = p;
          const d = engine?.duration || 1;
          (phRead.firstElementChild as HTMLElement).style.width = `${Math.min(100, (p / d) * 100)}%`;
        });
        await select(current);
        if (ctxRef !== ctx) return; // unmounted during the stab render
        ready = true;
        ctx.setStatus('Scratch ready — press the platter and spin.');
      } catch (err) {
        ready = false;
        host.append(el('p', { class: 'note warn' }, [
          `AudioWorklet unavailable: ${(err as Error).message}. The scratch scene needs AudioWorklet; nothing else in the app does.`,
        ]));
        ctx.setStatus('Scratch unavailable — this browser did not load the worklet module.');
      }

      // rate readout tick
      const tick = (): void => {
        rateRead.textContent = `${adapter.current.toFixed(2)}x`;
        if (ctxRef) requestAnimationFrame(tick);
      };
      tick();
    },

    update(frame: FeatureFrame, control: ControlState) {
      if (!engine || !ready) return;
      // The platter angle rides on the frame itself: the pad writes its drag
      // angle here and the camera writes the wrist angle about frame centre, so
      // the scratch rate law is fed the same quantity either way. The GRAB is
      // the vocabulary's pinch — the adapter watches control.pinch transitions.
      const intent = adapter.update(frame, control, cut);
      last = intent;
      engine.setRate(intent.rate);
      // Amplitude gates everything downstream — the scene's Level slider is a
      // trim inside the gesture's gate, not a way around it.
      engine.setLevel(level * intent.level);
    },

    readouts() {
      return [
        ['rate', last ? `${last.rate.toFixed(2)}x` : '—'],
        ['held', adapter.isHeld ? 'yes' : 'no'],
        ['cut', cut ? 'muted' : 'open'],
        ['head', `${playhead.toFixed(3)}s`],
      ];
    },

    unmount() { engine?.stop(); engine?.dispose(); engine = null; adapter.reset(); ctxRef = null; },
  };
}
